import React, {memo, useCallback, useState} from 'react';
import Dropzone from 'react-dropzone';
import {useDispatch, useSelector} from 'react-redux';
import {requestOK} from '~/utils/thunk-api';
import {AppDispatch} from '~/redux/root-store';
import {RootState} from '~/redux/root-reducer';
import {UiImage} from '~/components/elements/UiImage';
import {uploadFile} from '~/redux/apps/thunk';
import {UiIcon} from '~/components/elements/UiIcon';
import {FiUpload} from 'react-icons/all';
import {Row, UiBox, UiCenter, UiSpinner, UiText} from '~/components/elements/index';
import {CenterProps} from '@chakra-ui/react';
import {useNotification} from '~/hooks';
import {dataURIToBlob, resizeFile} from '~/utils/helpers';

export interface UploadResponse {
  fileName: string;
  fileType: string;
  accessUrl: string;
}

type AcceptType = 'VIDEO' | 'PDF' | 'IMAGE' | 'AUDIO';

interface Props {
  type: string;
  placeholder?: string | null;
  contentProps?: CenterProps;
  onSuccess: (arg: UploadResponse[]) => any;
  onRemove?: (f: UploadResponse) => void;
  acceptType?: AcceptType[];
  multiple?: boolean;
  preview?: boolean;
  limit?: number;
  endpoint?: string;
}

const getAcceptFileTypes = (types: AcceptType[]) => {
  const map = {
    VIDEO: 'video/*',
    AUDIO: 'audio/*',
    IMAGE: 'image/*',
    PDF: 'application/pdf',
  };
  return types.map(type => map[type]);
};

const maxSize = 10485760; // 1048576 Bytes * 10 = 10M

const defaultContentProps = {py: 8, px: 5};
export const AppDropzone = memo(function AppDropzone({
  type,
  onSuccess,
  preview = true,
  multiple = false,
  acceptType = ['PDF', 'IMAGE', 'VIDEO'],
  placeholder = 'Drag and Drop / click to select file',
  contentProps = {},
  limit = 1,
  endpoint,
  onRemove,
}: Props) {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);
  const {loading} = useSelector((state: RootState) => state.ui[uploadFile.typePrefix] || {});

  const [uploadedFiles, setUploadedFiles] = useState<UploadResponse[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  const onDrop = useCallback(
    async acceptedFiles => {
      if (!acceptedFiles || acceptedFiles.length === 0) {
        return;
      }

      if (acceptedFiles.length > limit) {
        setErrorMessage(`You can upload only ${limit} files`);
        return;
      }

      let res: UploadResponse[] = [];

      for await (const file of acceptedFiles) {
        let fileToUpload = file;
        if (file.size > maxSize) {
          const resizedImage: any = await resizeFile(file, file.type);
          fileToUpload = dataURIToBlob(resizedImage);
        }
        const resAction = await dispatch(
          uploadFile({
            file: fileToUpload,
            fileName: file.name,
            module: type,
            endpoint: endpoint,
          }),
        );
        if (requestOK(resAction, uploadFile)) {
          // @ts-ignore
          res.push({fileName: file.name, fileType: file.type, accessUrl: resAction?.payload?.accessUrl});
        }
      }
      res = multiple ? [...(uploadedFiles || []), ...res] : res;
      setUploadedFiles(res);
      onSuccess(res);
      setErrorMessage('');
      return;
    },
    [limit, multiple, uploadedFiles, onSuccess, dispatch, type, endpoint],
  );

  const onRemovePreview = (f: UploadResponse) => {
    setUploadedFiles(uploadedFiles.filter(file => file.accessUrl !== f.accessUrl));
    if (onRemove) onRemove(f);
  };

  const renderPreview = () => {
    if (!preview || !uploadedFiles || uploadedFiles.length === 0) {
      return null;
    }
    return (
      <Row flexDirection="row" flexWrap="wrap" mt={3}>
        {uploadedFiles?.map((f: UploadResponse, i) => {
          if (!f.fileType.includes('image')) {
            return null;
          }
          return (
            <UiImage
              boxSize="100px"
              url={f.accessUrl}
              containerClasses="mb-2"
              key={`image-index-${f.accessUrl}`}
              onClose={() => onRemovePreview(f)}
              mr="2"
              objectFit="contain"
            />
          );
        })}
      </Row>
    );
  };

  const renderPlaceholder = () => {
    if (placeholder === null) {
      return null;
    }
    return (
      <UiText fontSize="md" fontWeight="semibold">
        {placeholder}
      </UiText>
    );
  };

  return (
    <UiBox cursor="pointer">
      <Dropzone onDrop={onDrop} accept={getAcceptFileTypes(acceptType)} multiple={multiple}>
        {({getRootProps, getInputProps}) => (
          <div {...getRootProps()}>
            <UiCenter borderWidth={1} {...{...defaultContentProps, ...contentProps}}>
              {loading && (
                <UiBox>
                  <UiSpinner emptyColor="gray.200" size="lg" />
                </UiBox>
              )}

              <UiIcon as={FiUpload} boxSize={'25px'} mr={1} />
              {renderPlaceholder()}
              <input {...getInputProps()} />
            </UiCenter>
          </div>
        )}
      </Dropzone>
      {renderPreview()}
      {errorMessage && (
        <UiCenter>
          <UiText fontSize="xs" color="red" mt={1}>
            {errorMessage}
          </UiText>
        </UiCenter>
      )}
    </UiBox>
  );
});
